import { useCallback, useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { healthConditionsSchema } from "~/schemas/SignUp/HealthConditions";
import { SegmentedProgress } from "~/shared/components/ui/SegmentedProgress";
import { useFunnelTracking } from "~/shared/hooks/useFunnelTracking";
import {
  HEALTH_CONCERNS,
  MAX_HEALTH_CONDITIONS,
} from "~/shared/utils/constants";
import { SignUpFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { tw } from "~/utils";
import { ActionButtons } from "../ActionButtons";
import { ErrorMessage } from "../ErrorMessage";
import { FormContentContainer } from "../FormContentContainer";

interface HealthConditionsFormProps extends SignUpFormProps {
  stepId?: string;
}

export const HealthConditionsForm = ({
  onPrev,
  onNext,
  stepId = "health_conditions",
}: HealthConditionsFormProps) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [stuckItems, setStuckItems] = useState<string[]>([]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const stickyAreaRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const originalPositions = useRef<Map<string, number>>(new Map());

  const { setPatientField, patient } = useSignUpStore();
  const { conditions } = patient;

  const { trackComplete, trackBack, trackErrors } = useFunnelTracking({
    step: "health_conditions",
  });

  useEffect(() => {
    if (conditions.length > 0) {
      setSelectedOptions(conditions);
    }
  }, [conditions]);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof healthConditionsSchema>>({
    mode: "onBlur",
    resolver: zodResolver(healthConditionsSchema),
    defaultValues: {
      healthConditions: conditions || [],
    },
  });

  // Capture original positions after render
  useEffect(() => {
    const capturePositions = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      HEALTH_CONCERNS.forEach((option) => {
        const element = itemRefs.current.get(option);
        if (element) {
          const containerRect = container.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          originalPositions.current.set(
            option,
            elementRect.top - containerRect.top + container.scrollTop,
          );
        }
      });
    };

    // Delay to ensure DOM is ready
    const timer = setTimeout(capturePositions, 100);
    return () => clearTimeout(timer);
  }, []);

  // Handle scroll to determine stuck items
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const stickyAreaHeight = stickyAreaRef.current?.offsetHeight || 0;
      const scrollTop = container.scrollTop;

      // Items above this threshold should stick
      const threshold = scrollTop + stickyAreaHeight + 8;

      const newStuckItems: string[] = [];

      selectedOptions.forEach((item) => {
        const originalPos = originalPositions.current.get(item);
        if (originalPos !== undefined && originalPos < threshold) {
          newStuckItems.push(item);
        }
      });

      // Sort by original position
      newStuckItems.sort((a, b) => {
        const posA = originalPositions.current.get(a) || 0;
        const posB = originalPositions.current.get(b) || 0;
        return posA - posB;
      });

      setStuckItems(newStuckItems);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [selectedOptions]);

  const toggleCheckbox = (option: string) => {
    let newSelected: string[];

    if (selectedOptions.includes(option)) {
      newSelected = selectedOptions.filter((item) => item !== option);
      setStuckItems((prev) => prev.filter((item) => item !== option));
    } else if (selectedOptions.length < MAX_HEALTH_CONDITIONS) {
      newSelected = [...selectedOptions, option];
    } else {
      return;
    }

    setSelectedOptions(newSelected);
    setValue("healthConditions", newSelected, { shouldValidate: true });
  };

  const onSubmit: SubmitHandler<
    z.infer<typeof healthConditionsSchema>
  > = async (data) => {
    setPatientField("conditions", data.healthConditions);
    await trackComplete({ conditions: data.healthConditions });
    onNext();
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientField("conditions", []);
    trackBack();
    onPrev();
  };

  const canProceed =
    selectedOptions.length > 0 &&
    selectedOptions.length <= MAX_HEALTH_CONDITIONS;

  const progressPercentage = Math.min(
    (selectedOptions.length / MAX_HEALTH_CONDITIONS) * 100,
    100,
  );

  const registerRef = useCallback(
    (option: string) => (el: HTMLDivElement | null) => {
      if (el) {
        itemRefs.current.set(option, el);
      } else {
        itemRefs.current.delete(option);
      }
    },
    [],
  );

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="flex max-h-[calc(100vh-32px)] min-h-0 flex-col overflow-hidden rounded-lg shadow-xl sm:max-h-[calc(100vh-64px)]"
    >
      {/* Use showProgress={false} since we put it inside the scroll area */}
      <FormContentContainer
        showProgress={false}
        noPadding
        className="min-h-0 flex-1 overflow-hidden"
      >
        {/* Scrollable area - fills available space */}
        <div
          ref={scrollContainerRef}
          className="relative min-h-0 w-full flex-1 overflow-y-auto px-6 pt-6 sm:px-10 sm:pt-8"
        >
          {/* Header section - scrolls away */}
          <div className="flex flex-col items-start gap-4">
            <SegmentedProgress currentStepId={stepId} className="w-full" />

            <HipaaLogo className="size-16 self-center sm:size-20" />

            <div>
              <h1 className="font-display text-xl sm:text-2xl">
                What can we support you with?
              </h1>
              <span className="text-base text-gray-600 sm:text-sm">
                (Select at least 1, up to {MAX_HEALTH_CONDITIONS})
              </span>
            </div>
          </div>

          {/* Selection counter + chips - sticky when scrolled */}
          <div
            ref={stickyAreaRef}
            className="sticky top-0 z-10 -mx-6 mt-2 bg-white px-6 pt-2 pb-3 sm:-mx-10 sm:px-10"
          >
            {/* Selection counter */}

            <div className="w-full rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Selected:</span>
                <span
                  className={tw(
                    "font-medium",
                    selectedOptions.length === 0 && "text-gray-400",
                    selectedOptions.length > 0 && "text-green-600",
                  )}
                >
                  {selectedOptions.length} of {MAX_HEALTH_CONDITIONS}
                </span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200">
                <div
                  className={tw(
                    "h-full transition-all duration-300",
                    selectedOptions.length === 0
                      ? "bg-gray-300"
                      : "bg-green-500",
                  )}
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>

            {/* Sticky chips - only when items are stuck */}
            {stuckItems.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {stuckItems.map((option) => (
                  <button
                    key={`stuck-${option}`}
                    type="button"
                    onClick={() => toggleCheckbox(option)}
                    className="flex items-center gap-1.5 rounded-full border-2 border-green-500 bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700 transition-all hover:bg-green-100"
                  >
                    <span className="max-w-[200px] truncate">{option}</span>
                    <svg
                      className="size-4 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Options list */}
          <div className="mt-4 grid w-full grid-cols-1 gap-2 pb-6 sm:grid-cols-2 sm:gap-3 sm:pb-8">
            {HEALTH_CONCERNS.map((option) => {
              const isSelected = selectedOptions.includes(option);
              const isStuck = stuckItems.includes(option);
              const isDisabled =
                selectedOptions.length >= MAX_HEALTH_CONDITIONS && !isSelected;

              return (
                <div
                  key={option}
                  ref={registerRef(option)}
                  className={tw(
                    "transition-all duration-200",
                    isStuck && "invisible h-0 overflow-hidden opacity-0",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => toggleCheckbox(option)}
                    disabled={isDisabled}
                    className={tw(
                      "flex w-full cursor-pointer items-center rounded-lg border-2 p-3 text-left transition-all",
                      "min-h-[48px]",
                      isSelected && "border-green-500 bg-green-50",
                      !isSelected &&
                        !isDisabled &&
                        "border-gray-200 hover:border-gray-300 hover:bg-gray-50",
                      isDisabled &&
                        "cursor-not-allowed border-gray-100 bg-gray-50 opacity-50",
                    )}
                  >
                    {/* Checkbox */}
                    <div
                      className={tw(
                        "mr-3 flex size-5 shrink-0 items-center justify-center rounded border-2 transition-all",
                        isSelected
                          ? "border-green-500 bg-green-500"
                          : "border-gray-300",
                      )}
                    >
                      {isSelected && (
                        <svg
                          className="size-3 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                    {/* Label */}
                    <span className="text-sm leading-tight font-medium text-gray-900">
                      {option}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>

          {errors.healthConditions && (
            <ErrorMessage message={errors.healthConditions.message ?? ""} />
          )}
        </div>
      </FormContentContainer>
      <ActionButtons
        onPrev={handlePrev}
        isNextDisabled={!canProceed}
        helperText="Select at least one condition to continue"
      />
    </form>
  );
};
